import { useCallback, useRef, useState } from 'react';
import { getNotificationModuleCapabilities } from '../config/notificationCapabilities';
import { useNotificationCount } from '../context/NotificationCountContext';
import {
  listNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '../services/notificationService';
import type {
  NotificationFilters,
  NotificationListItem,
} from '../types/notification';
import { notificationErrorMessage } from '../utils/notificationErrors';

const DEFAULT_FILTERS: NotificationFilters = {
  page: 1,
  pageSize: 20,
};

export function createDefaultNotificationFilters(): NotificationFilters {
  return { ...DEFAULT_FILTERS };
}

export function useNotifications(permissions: readonly string[] = []) {
  const capabilities = getNotificationModuleCapabilities(permissions);
  const {
    unreadCount,
    countUnavailable,
    reloadUnreadCount,
    setUnreadCountAfterConfirmedRead,
  } = useNotificationCount();

  const [items, setItems] = useState<NotificationListItem[]>([]);
  const [filters, setFilters] = useState<NotificationFilters>(
    createDefaultNotificationFilters(),
  );
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyUnavailable, setHistoryUnavailable] = useState(
    !capabilities.canViewList,
  );
  const [markReadError, setMarkReadError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const markingIds = useRef<Set<number>>(new Set());
  const markAllBusy = useRef(false);
  const loadMoreBusy = useRef(false);
  const requestId = useRef(0);
  const filtersRef = useRef(filters);
  const pageRef = useRef(1);

  const loadNotifications = useCallback(
    async (refreshing = false) => {
      const currentRequest = ++requestId.current;
      refreshing ? setIsRefreshing(true) : setIsLoading(true);
      setError(null);
      setStatusMessage(null);

      if (!capabilities.canViewList) {
        if (currentRequest !== requestId.current) return;
        setItems([]);
        setPage(1);
        pageRef.current = 1;
        setHasMore(false);
        setHistoryUnavailable(true);
        await reloadUnreadCount();
        if (currentRequest !== requestId.current) return;
        setIsLoading(false);
        setIsRefreshing(false);
        return;
      }

      try {
        const result = await listNotifications({
          ...filtersRef.current,
          page: 1,
          pageSize: filtersRef.current.pageSize || 20,
        });
        if (currentRequest !== requestId.current) return;
        setHistoryUnavailable(false);
        setItems(result.items);
        setPage(result.page);
        pageRef.current = result.page;
        setHasMore(result.page < result.totalPages);
        if (result.unreadCount != null) {
          setUnreadCountAfterConfirmedRead(Math.max(0, result.unreadCount));
        } else {
          await reloadUnreadCount();
        }
      } catch (caught) {
        if (currentRequest !== requestId.current) return;
        setItems([]);
        setHasMore(false);
        setHistoryUnavailable(false);
        setError(
          notificationErrorMessage(
            caught,
            'Unable to load notifications.',
          ),
        );
      } finally {
        if (currentRequest === requestId.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [
      capabilities.canViewList,
      reloadUnreadCount,
      setUnreadCountAfterConfirmedRead,
    ],
  );

  const refreshNotifications = useCallback(async () => {
    await loadNotifications(true);
  }, [loadNotifications]);

  const loadMore = useCallback(async () => {
    if (!capabilities.canViewList) return;
    if (!hasMore || loadMoreBusy.current || isLoadingMore) return;

    loadMoreBusy.current = true;
    setIsLoadingMore(true);
    const nextPage = pageRef.current + 1;
    const currentRequest = ++requestId.current;
    try {
      const result = await listNotifications({
        ...filtersRef.current,
        page: nextPage,
        pageSize: filtersRef.current.pageSize || 20,
      });
      if (currentRequest !== requestId.current) return;
      setItems(current => {
        const seen = new Set(current.map(item => item.notificationId));
        const merged = [...current];
        for (const item of result.items) {
          if (!seen.has(item.notificationId)) merged.push(item);
        }
        return merged;
      });
      setPage(result.page);
      pageRef.current = result.page;
      setHasMore(result.page < result.totalPages);
    } catch (caught) {
      if (currentRequest === requestId.current) {
        setError(
          notificationErrorMessage(
            caught,
            'Unable to load more notifications.',
          ),
        );
      }
    } finally {
      loadMoreBusy.current = false;
      if (currentRequest === requestId.current) {
        setIsLoadingMore(false);
      }
    }
  }, [capabilities.canViewList, hasMore, isLoadingMore]);

  const applyFilters = useCallback(
    (next: NotificationFilters) => {
      const normalized = { ...next, page: 1 };
      setFilters(normalized);
      filtersRef.current = normalized;
      setPage(1);
      pageRef.current = 1;
      setHasMore(false);
      if (!capabilities.canViewList) {
        setHistoryUnavailable(true);
        setItems([]);
        return;
      }
      setHistoryUnavailable(false);
      loadNotifications(false).catch(() => undefined);
    },
    [capabilities.canViewList, loadNotifications],
  );

  const resetFilters = useCallback(() => {
    applyFilters(createDefaultNotificationFilters());
  }, [applyFilters]);

  const markRead = useCallback(
    async (notificationId: number) => {
      if (!capabilities.canMarkRead) return null;
      if (!Number.isInteger(notificationId) || notificationId <= 0) {
        setMarkReadError('Check the notification request.');
        return null;
      }

      const existing = items.find(
        item => item.notificationId === notificationId,
      );
      if (existing?.isRead) return existing;

      if (markingIds.current.has(notificationId)) return null;
      markingIds.current.add(notificationId);
      setMarkReadError(null);

      try {
        const result = await markNotificationAsRead(notificationId);
        setItems(current =>
          current.map(item =>
            item.notificationId === notificationId
              ? {
                  ...item,
                  isRead: true,
                  readAt: result.readAt ?? item.readAt,
                }
              : item,
          ),
        );

        const wasUnread = existing ? !existing.isRead : true;
        if (wasUnread && unreadCount != null) {
          setUnreadCountAfterConfirmedRead(Math.max(0, unreadCount - 1));
        } else {
          await reloadUnreadCount();
        }
        return result;
      } catch (caught) {
        setMarkReadError(
          notificationErrorMessage(
            caught,
            'Unable to mark the notification as read.',
          ),
        );
        return null;
      } finally {
        markingIds.current.delete(notificationId);
      }
    },
    [
      capabilities.canMarkRead,
      items,
      reloadUnreadCount,
      setUnreadCountAfterConfirmedRead,
      unreadCount,
    ],
  );

  const markAllRead = useCallback(async () => {
    if (!capabilities.canMarkAllRead) return false;
    if (unreadCount === null || unreadCount <= 0) return false;
    if (markAllBusy.current || isMarkingAllRead) return false;

    markAllBusy.current = true;
    setIsMarkingAllRead(true);
    setError(null);
    setStatusMessage(null);

    try {
      const result = await markAllNotificationsAsRead();
      setItems(current =>
        current.map(item => ({
          ...item,
          isRead: true,
          readAt: item.readAt ?? new Date().toISOString(),
        })),
      );
      setUnreadCountAfterConfirmedRead(Math.max(0, result.unreadCount));
      return true;
    } catch {
      await Promise.allSettled([
        reloadUnreadCount(),
        loadNotifications(true),
      ]);
      setStatusMessage('Checking the latest notification status.');
      return false;
    } finally {
      markAllBusy.current = false;
      setIsMarkingAllRead(false);
    }
  }, [
    capabilities.canMarkAllRead,
    isMarkingAllRead,
    loadNotifications,
    reloadUnreadCount,
    setUnreadCountAfterConfirmedRead,
    unreadCount,
  ]);

  const retry = useCallback(async () => {
    await loadNotifications(false);
  }, [loadNotifications]);

  return {
    items,
    filters,
    page,
    unreadCount,
    countUnavailable,
    isLoading,
    isRefreshing,
    isLoadingMore,
    isMarkingAllRead,
    hasMore,
    error,
    historyUnavailable,
    markReadError,
    statusMessage,
    capabilities,
    loadNotifications,
    refreshNotifications,
    loadMore,
    applyFilters,
    resetFilters,
    markRead,
    markAllRead,
    retry,
  };
}
